import { keepPreviousData } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { z } from 'zod'

import {
	useGetDashboardFeatures,
	useGetProductReport,
	useGetRevenueReport,
	useListSalesChannels
} from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { Select } from '@/components/base/select'
import { Text } from '@/components/base/text'
import { Thumbnail } from '@/components/base/thumbnail'
import { SingleColumnPage } from '@/components/page-layout'
import { SalesChannelMultiSelect } from '@/components/sales-channel-multi-select'
import { Pagination } from '@/components/table/pagination'
import { Table, type TableColDef, useServerSort } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'

import {
	analyticsPeriodSearchSchema,
	COMPARE_LAST_YEAR,
	ComparisonControls,
	type ComparisonMode,
	CONTROL_WIDTH,
	PeriodJumpPicker,
	ProductMultiSelect,
	resolveComparePeriodParam,
	selectedPeriodStart,
	startOfPeriodUtc
} from '../analytics-shared'
import {
	allowedGroupBy,
	type Amount,
	ColumnsPicker,
	defaultGroupBy,
	MetricCell,
	type RevenueReportGroupBy,
	toMajor,
	useBucketLabel
} from './report-shared'

const PAGE_SIZE = 50

export const Route = createFileRoute('/_authenticated/_general/analytics/reports/')({
	component: ReportsPage,
	validateSearch: analyticsPeriodSearchSchema.extend({
		// A time bucket (revenue report) or 'product' (product report); omit for the interval default.
		rows: z.enum(['hour', 'day', 'week', 'month', 'product']).optional(),
		// Selected metric column ids; omit for the row type's defaults.
		columns: z.array(z.string()).optional(),
		// Product-report only:
		productUuids: z.array(z.string()).optional(),
		sortBy: z.enum(['revenue', 'quantity']).optional(),
		sortOrder: z.enum(['asc', 'desc']).optional(),
		offset: z.coerce.number().int().min(0).optional()
	})
})

type RowsType = RevenueReportGroupBy | 'product'
type Cell = { value: number | null; compared: number | null }
type GridRow = { key: string; label: string; imageUrl?: string | null; partial?: boolean; cells: Record<string, Cell> }

const TIME_COLUMN_IDS = ['orders', 'revenue', 'refunds', 'aov'] as const
const PRODUCT_COLUMN_IDS = ['quantity', 'revenue', 'orders', 'share'] as const
const TIME_DEFAULT_COLUMNS = ['orders', 'revenue', 'aov']
const PRODUCT_DEFAULT_COLUMNS = ['quantity', 'revenue', 'orders']
// Columns the product report can sort on server-side (ids match the API's sort fields).
const PRODUCT_SORTABLE = ['revenue', 'quantity']

const capitalize = <T extends RevenueReportGroupBy>(value: T) =>
	(value.charAt(0).toUpperCase() + value.slice(1)) as Capitalize<T>

function ReportsPage() {
	const { L, locale } = useI18n()
	const R = L.Analytics.Reports
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const bucketLabel = useBucketLabel()
	const offset = search.offset ?? 0

	const interval = search.interval
	const rowsType: RowsType = resolveRowsType(search.rows, interval)
	const isProduct = rowsType === 'product'

	// Comparison mode is explicit local state (a channel subject means channel mode, an absent
	// comparePeriod means off) so the user can enter channel mode before picking a subject.
	const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(() => {
		if ((search.compareChannels?.length ?? 0) > 0) return 'channel'
		if (search.comparePeriod === undefined) return 'none'
		return 'period'
	})

	// Comparison is off by default on reports: an absent `comparePeriod` means "no comparison" here,
	// the sentinel `'previous'` means previous period, and channel comparison clears the period one.
	const apiQuery = useMemo(() => reportApiQuery(search), [search])
	const revenueReport = useGetRevenueReport(
		{ ...apiQuery, groupBy: isProduct ? undefined : rowsType },
		{ enabled: !isProduct, placeholderData: keepPreviousData }
	)
	const productReport = useGetProductReport(
		{
			...apiQuery,
			...(search.productUuids?.length ? { productUuids: search.productUuids } : {}),
			sortBy: search.sortBy ?? 'revenue',
			sortOrder: search.sortOrder ?? 'desc',
			limit: PAGE_SIZE,
			offset
		},
		{ enabled: isProduct, placeholderData: keepPreviousData }
	)
	const { data: dashboardFeatures } = useGetDashboardFeatures()
	const { data: salesChannelList, isLoading: channelsLoading } = useListSalesChannels()
	const salesChannels = salesChannelList ?? []

	const revenueData = revenueReport.data
	const productData = productReport.data
	const hasComparison = (isProduct ? productData?.hasComparison : revenueData?.hasComparison) ?? false
	const currencyCode =
		(isProduct ? productData?.currencyCode : revenueData?.currencyCode) ??
		dashboardFeatures?.reportingCurrencyCode ??
		'EUR'

	// Lower bound for the period pickers: the first completed sale (UTC start-of-day), so merchants
	// can't page back into empty pre-history. Undefined when there are no sales yet (no bound).
	const earliestSelectable =
		dashboardFeatures?.earliestAnalyticsDay !== undefined
			? new Date(dashboardFeatures.earliestAnalyticsDay)
			: undefined

	const formatMoney = useCallback(
		(value: number) => L.Amount.Default({ amount: { value: Math.round(value * 100), precision: 2 }, currencyCode }),
		[L, currencyCode]
	)
	const formatCount = useCallback(
		(value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
		[locale]
	)
	const formatPercent = useCallback(
		(value: number) => `${value.toLocaleString(locale, { maximumFractionDigits: 1 })}%`,
		[locale]
	)

	const columnMeta = useMemo<Record<string, { label: string; format: (v: number) => string }>>(
		() => ({
			orders: { label: R.Columns.Orders(), format: formatCount },
			revenue: { label: isProduct ? R.Columns.GrossSales() : R.Columns.Revenue(), format: formatMoney },
			refunds: { label: R.Columns.Refunds(), format: formatMoney },
			aov: { label: R.Columns.AverageOrderValue(), format: formatMoney },
			quantity: { label: R.Columns.Quantity(), format: formatCount },
			share: { label: R.Columns.Share(), format: formatPercent }
		}),
		[isProduct, R, formatCount, formatMoney, formatPercent]
	)

	const availableColumnIds = isProduct ? PRODUCT_COLUMN_IDS : TIME_COLUMN_IDS
	const defaultColumns = isProduct ? PRODUCT_DEFAULT_COLUMNS : TIME_DEFAULT_COLUMNS
	const selectedColumns = useMemo(() => {
		const picked = (search.columns ?? defaultColumns).filter(id =>
			(availableColumnIds as readonly string[]).includes(id)
		)
		return picked.length > 0 ? picked : defaultColumns
	}, [search.columns, availableColumnIds, defaultColumns])

	// Build the grid rows + totals for the active report.
	const { rows, totalsRow } = useMemo<{ rows: GridRow[]; totalsRow: GridRow[] }>(() => {
		if (isProduct) {
			if (!productData) return { rows: [], totalsRow: [] }
			const bodyRows = productData.rows.map(row => ({
				key: row.productUuid,
				label: row.productTitle,
				imageUrl: row.imageUrl,
				cells: {
					quantity: { value: row.quantity, compared: row.comparison?.quantity ?? null },
					revenue: {
						value: toMajor(row.revenue),
						compared: row.comparison ? toMajor(row.comparison.revenue) : null
					},
					orders: { value: row.orderCount, compared: row.comparison?.orderCount ?? null },
					share: { value: row.revenueShare, compared: null }
				}
			}))
			const t = productData.totals
			const c = productData.comparisonTotals
			return {
				rows: bodyRows,
				totalsRow: [
					{
						key: '__total__',
						label: R.Total(),
						cells: {
							quantity: { value: t.quantity, compared: c?.quantity ?? null },
							revenue: { value: toMajor(t.revenue), compared: c ? toMajor(c.revenue) : null },
							orders: { value: t.orderCount, compared: c?.orderCount ?? null },
							share: { value: null, compared: null }
						}
					}
				]
			}
		}

		if (!revenueData) return { rows: [], totalsRow: [] }
		const groupBy = revenueData.groupBy
		const isHour = groupBy === 'hour'
		const timeCells = (m: {
			orderCount: number
			revenue: Amount
			refundTotal: Amount
			averageOrderValue: Amount
		}): Record<string, Cell> => ({
			orders: { value: m.orderCount, compared: null },
			revenue: { value: toMajor(m.revenue), compared: null },
			refunds: { value: toMajor(m.refundTotal), compared: null },
			aov: { value: toMajor(m.averageOrderValue), compared: null }
		})
		const bodyRows = revenueData.rows.map((pt, i) => {
			const cmp = hasComparison ? revenueData.comparisonRows?.[i] : undefined
			return {
				key: pt.date,
				label: bucketLabel(pt.date, groupBy),
				partial: pt.partial,
				cells: {
					orders: { value: pt.orderCount, compared: cmp?.orderCount ?? null },
					revenue: { value: toMajor(pt.revenue), compared: cmp ? toMajor(cmp.revenue) : null },
					// The rollup has no per-hour refunds, so hourly rows omit them.
					refunds: {
						value: isHour ? null : toMajor(pt.refundTotal),
						compared: cmp && !isHour ? toMajor(cmp.refundTotal) : null
					},
					aov: { value: toMajor(pt.averageOrderValue), compared: cmp ? toMajor(cmp.averageOrderValue) : null }
				}
			}
		})
		const totalCells = timeCells(revenueData.totals)
		if (hasComparison) {
			const ct = timeCells(revenueData.comparisonTotals)
			for (const id of TIME_COLUMN_IDS) totalCells[id].compared = ct[id].value
		}
		return { rows: bodyRows, totalsRow: [{ key: '__total__', label: R.Total(), cells: totalCells }] }
	}, [isProduct, productData, revenueData, hasComparison, bucketLabel, R])

	const serverSort = {
		...useServerSort(PRODUCT_SORTABLE),
		sortBy: search.sortBy ?? 'revenue',
		sortOrder: search.sortOrder ?? 'desc'
	}

	const columnDefs = useMemo<TableColDef<GridRow>[]>(() => {
		const labelCol: TableColDef<GridRow> = {
			colId: 'label',
			headerName: isProduct ? R.Columns.Product() : R.Columns.Period(),
			// Time rows sort chronologically on the bucket key; product rows aren't name-sortable.
			sortable: !isProduct,
			valueGetter: p => (isProduct ? p.data?.label : p.data?.key),
			valueFormatter: p => p.data?.label ?? '',
			cellRenderer: (p: { data?: GridRow; node?: { rowPinned?: string | null } }) => {
				// Match the table's default cell typography (txt-compact-small) so this column lines up with
				// the metric cells and every other grid in the app, rather than Text's larger `base` default.
				// The wrapping flex row (h-full items-center) vertically centres the label in the 48px row,
				// mirroring MetricCell, instead of letting the <p> sit at the top of the cell.
				if (isProduct) {
					if (p.node?.rowPinned) {
						return (
							<div className='flex h-full items-center'>
								<Text size='small' leading='compact' weight='plus'>
									{p.data?.label}
								</Text>
							</div>
						)
					}
					return (
						<div className='flex h-full items-center gap-2'>
							<Thumbnail src={p.data?.imageUrl} size='small' />
							<Text size='small' leading='compact' className='truncate'>
								{p.data?.label}
							</Text>
						</div>
					)
				}
				return (
					<div className='flex h-full items-center'>
						<Text
							size='small'
							leading='compact'
							weight={p.node?.rowPinned ? 'plus' : 'regular'}
							className='truncate'
						>
							{p.data?.label}
							{p.data?.partial ? ` ${L.Analytics.InProgress()}` : ''}
						</Text>
					</div>
				)
			},
			// The period label is short ("Mon, Oct 6", "October 2026"), so it only needs a modest share;
			// the metric columns (revenue especially) get the extra width instead.
			minWidth: isProduct ? 220 : 130,
			flex: isProduct ? 2 : 1
		}

		const metricCols = selectedColumns.map((id): TableColDef<GridRow> => {
			const meta = columnMeta[id]
			// Revenue is the headline figure, so it gets the widest track; the rest share the remainder.
			const isRevenue = id === 'revenue'
			// Comparison cells need room for "value → value +%" on one line.
			const minWidth = hasComparison ? 180 : 120
			return {
				colId: id,
				headerName: meta.label,
				sortable: !isProduct || PRODUCT_SORTABLE.includes(id),
				valueGetter: p => p.data?.cells?.[id]?.value ?? null,
				useValueFormatterForExport: false,
				cellRenderer: (p: { data?: GridRow }) => (
					<MetricCell
						value={p.data?.cells?.[id]?.value}
						compared={p.data?.cells?.[id]?.compared}
						format={meta.format}
					/>
				),
				minWidth: isRevenue ? minWidth + 40 : minWidth,
				flex: isRevenue ? 2 : 1
			}
		})

		return [labelCol, ...metricCols]
	}, [isProduct, selectedColumns, columnMeta, hasComparison, R, L])

	const patchSearch = (patch: Partial<typeof search>, resetOffset = true) =>
		void navigate({ search: prev => ({ ...prev, ...patch, ...(resetOffset ? { offset: undefined } : {}) }) })

	const changeInterval = (nextInterval: typeof interval) => {
		// A new interval re-anchors the period and may invalidate a time-bucket rows choice.
		const keepRows = isProduct || allowedGroupBy(nextInterval).includes(rowsType as RevenueReportGroupBy)
		// A specific comparison date was tied to the old interval's period math, so drop it back to the
		// previous period. 'previous', same-period-last-year, and off (undefined) stay meaningful.
		const comparePeriod = isSpecificCompare(search.comparePeriod) ? 'previous' : search.comparePeriod
		patchSearch({
			interval: nextInterval,
			periodStart: undefined,
			rows: keepRows ? search.rows : undefined,
			comparePeriod
		})
	}
	const changeRows = (next: RowsType) => {
		// Switching the row type resets the column selection and any product-only params.
		patchSearch({
			rows: next,
			columns: undefined,
			...(next === 'product' ? {} : { productUuids: undefined, sortBy: undefined, sortOrder: undefined })
		})
	}
	const changePeriod = (periodStart: string | undefined) => {
		// A specific comparison date must stay strictly before the selected period; once paging/jumping
		// would put it at or after the new period, fall back to the previous period. 'previous',
		// same-period-last-year, and off stay unchanged.
		let comparePeriod = search.comparePeriod
		if (isSpecificCompare(comparePeriod)) {
			const nextSelected = selectedPeriodStart(interval, periodStart)
			const compareStart = startOfPeriodUtc(interval, new Date(`${comparePeriod}T00:00:00.000Z`))
			if (compareStart.getTime() >= nextSelected.getTime()) comparePeriod = 'previous'
		}
		patchSearch({ periodStart, comparePeriod })
	}
	const changeComparePeriod = (cp: string | undefined) =>
		patchSearch({ comparePeriod: toComparePeriodSearch(cp), compareChannels: undefined })
	const changeCompareChannels = (uuids: string[]) =>
		patchSearch({ compareChannels: uuids.length > 0 ? uuids : undefined, comparePeriod: undefined })
	// Reports default to comparison off: period mode turns it on (previous period), channel mode turns
	// the period comparison off and waits for a subject, none clears both.
	const switchComparisonMode = (next: ComparisonMode) => {
		setComparisonMode(next)
		if (next === 'period') patchSearch({ comparePeriod: 'previous', compareChannels: undefined })
		else if (next === 'channel') patchSearch({ comparePeriod: undefined })
		else patchSearch({ comparePeriod: undefined, compareChannels: undefined })
	}

	const rowsOptions: Array<{ value: RowsType; label: string }> = [
		...allowedGroupBy(interval).map(gb => ({ value: gb, label: R.RowsOption[capitalize(gb)]() })),
		{ value: 'product', label: R.RowsOption.Product() }
	]

	const showPagination = isProduct && (productData?.totalCount ?? 0) > PAGE_SIZE

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={R.Title()} />

				<div className='flex flex-col gap-2 px-3 pb-3 sm:px-6 sm:pb-4'>
					{/* All toolbar controls share one evenly-sized, wrapping row: period (with interval
					    selector), channel filter, comparison picker, table breakdown, columns and (for
					    products) the product filter. They reflow onto further rows as the width shrinks. */}
					<div className='flex flex-wrap items-center gap-2'>
						<PeriodJumpPicker
							interval={interval}
							periodStart={search.periodStart}
							earliestSelectable={earliestSelectable}
							onChange={changePeriod}
							onIntervalChange={changeInterval}
							className={CONTROL_WIDTH}
						/>
						<SalesChannelMultiSelect
							salesChannels={salesChannels}
							value={search.channels ?? []}
							onChange={next => patchSearch({ channels: next.length > 0 ? next : undefined })}
							allLabel={L.Analytics.AllChannels()}
							isLoading={channelsLoading}
							className={CONTROL_WIDTH}
						/>
						<ComparisonControls
							interval={interval}
							periodStart={search.periodStart}
							earliestSelectable={earliestSelectable}
							mode={comparisonMode}
							onModeChange={switchComparisonMode}
							compareSelectValue={toCompareSelectValue(search.comparePeriod)}
							onCompareSelect={changeComparePeriod}
							channels={search.compareChannels ?? []}
							onChannelsChange={changeCompareChannels}
							salesChannels={salesChannels}
							channelsLoading={channelsLoading}
						/>
						<Select value={rowsType} onValueChange={value => changeRows(value as RowsType)}>
							<Select.Trigger className={`h-8 ${CONTROL_WIDTH}`} aria-label={R.Rows()}>
								<Select.Value />
							</Select.Trigger>
							<Select.Content>
								{rowsOptions.map(option => (
									<Select.Item key={option.value} value={option.value}>
										{option.label}
									</Select.Item>
								))}
							</Select.Content>
						</Select>
						<ColumnsPicker
							options={selectedColumnsOptions(availableColumnIds, columnMeta)}
							value={selectedColumns}
							onChange={ids => patchSearch({ columns: ids })}
							className={CONTROL_WIDTH}
						/>
						{isProduct && (
							<ProductMultiSelect
								value={search.productUuids ?? []}
								onChange={uuids => patchSearch({ productUuids: uuids.length ? uuids : undefined })}
								placeholder={R.Products.FilterProducts()}
								className={CONTROL_WIDTH}
							/>
						)}
					</div>
				</div>

				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<GridRow>
						className='w-full min-w-[640px] flex-1'
						data={rows}
						columnDefs={columnDefs}
						getRowId={row => row.key}
						serverSort={isProduct ? serverSort : undefined}
						pinnedBottomRowData={totalsRow}
						rowHeight={48}
						animateRows
						suppressCellFocus
					/>
				</div>

				{showPagination && (
					<div className='px-3 py-2 sm:px-6 sm:py-4'>
						<Pagination
							totalCount={productData?.totalCount}
							limit={PAGE_SIZE}
							offset={offset}
							onOffsetChange={next =>
								void navigate({ search: prev => ({ ...prev, offset: next || undefined }) })
							}
						/>
					</div>
				)}

				{isProduct && (
					<Text size='small' className='px-3 pb-4 text-ui-fg-muted sm:px-6'>
						{R.Products.GrossSalesNote()}
					</Text>
				)}
			</Container>
		</SingleColumnPage>
	)
}

/** Resolve the effective row type: a valid time bucket for the interval, else the interval default. */
function resolveRowsType(
	rows: RowsType | undefined,
	interval: ReturnType<typeof Route.useSearch>['interval']
): RowsType {
	if (rows === 'product') return 'product'
	if (rows && allowedGroupBy(interval).includes(rows)) return rows
	return defaultGroupBy(interval)
}

const selectedColumnsOptions = (
	ids: readonly string[],
	meta: Record<string, { label: string }>
): Array<{ id: string; label: string }> => ids.map(id => ({ id, label: meta[id].label }))

/**
 * A specific comparison date in the report search (a concrete `yyyy-MM-dd`), as opposed to the
 * off (`undefined`), previous-period (`'previous'`) and same-period-last-year sentinels, which stay
 * valid across interval/period changes and so are never revalidated.
 */
function isSpecificCompare(comparePeriod: string | undefined): comparePeriod is string {
	return comparePeriod !== undefined && comparePeriod !== 'previous' && comparePeriod !== COMPARE_LAST_YEAR
}

/** Report search `comparePeriod` → the value the shared {@link CompareSelect} expects (undefined = previous). */
function toCompareSelectValue(comparePeriod: string | undefined): string | undefined {
	if (comparePeriod === undefined) return 'none'
	if (comparePeriod === 'previous') return undefined
	return comparePeriod
}

/** {@link CompareSelect} value → the report search `comparePeriod` (absent = off, 'previous' = previous). */
function toComparePeriodSearch(cp: string | undefined): string | undefined {
	if (cp === undefined) return 'previous'
	if (cp === 'none') return undefined
	return cp
}

/** API query params for the report endpoints from the report search (comparison off by default). */
function reportApiQuery(search: ReturnType<typeof Route.useSearch>) {
	let comparePeriodApi: string | undefined
	if (search.comparePeriod === undefined) comparePeriodApi = 'none'
	else if (search.comparePeriod === 'previous') comparePeriodApi = undefined
	else comparePeriodApi = resolveComparePeriodParam(search.interval, search.periodStart, search.comparePeriod)

	return {
		interval: search.interval,
		...(search.periodStart ? { periodStart: search.periodStart } : {}),
		...(comparePeriodApi ? { comparePeriod: comparePeriodApi } : {}),
		...(search.channels && search.channels.length > 0 ? { salesChannelUuids: search.channels } : {}),
		...(search.compareChannels && search.compareChannels.length > 0
			? { compareSalesChannelUuids: search.compareChannels }
			: {})
	}
}
